import classNames from 'classnames';

import { useMemo } from 'react';
import { EntityType } from '@/app/shared/constants/globalConstants';
import { AssetSelect, StipsTypeSelect } from '@/app/shared/ui';
import type { StipType } from '@/app/shared/ui/StipsTypeSelect';
import AdditionalItemsUpload from './AdditionalItemsUpload';
import type { AssetDocument, AssetType, FileData, IStipsLocalState } from '../../types';
import type { Entities, StipRequestItems } from '../../uploadStips.services';
import type { SelectChangeEvent } from '@mui/material';

interface AdditionalItemsProps {
  fileName: string;
  selectedStipTypeId?: number | null;
  stipsTypes: StipType[];
  data: Record<string, FileData | undefined>;
  assetDocuments: AssetDocument[];
  assets: Entities | undefined;
  stipRequestItems: StipRequestItems[];
  stipsLocalState: IStipsLocalState;
  assetsLocalState?: string;
  handleFile: (event: React.ChangeEvent<HTMLInputElement>, type: string) => void;
  removeFile: (fileName: string, type: string) => void;
  handleChange: (event: SelectChangeEvent, fileName: string) => void;
  handleAssetChange: (event: SelectChangeEvent, fileName: string) => void;
}

function generateAssets(id: number, assets: Entities | undefined, assetDocuments: AssetDocument[]): AssetType[] | undefined {
  if (id === EntityType.CONTACT) {
    return assets?.contacts;
  }

  if (id === EntityType.ASSET) {
    const assetEntityIds = new Set(
      assetDocuments
        .filter((doc) => doc.entityTypeName === 'Asset')
        .map((doc) => doc.entityId)
    );

    return assets?.assets.filter((asset) => asset.entityId && assetEntityIds.has(asset.entityId));
  }

  if (id === EntityType.DEAL) {
    return assets?.deals;
  }

  return undefined;
}

function generateTitle(id: number): string {
  if (id === EntityType.CONTACT) {
    return 'Select Borrower Name';
  }

  if (id === EntityType.ASSET || id === EntityType.DEAL) {
    return 'Select Vehicle';
  }

  return '';
}

export default function AdditionalItems({
  selectedStipTypeId,
  stipsTypes,
  data,
  assetDocuments,
  assets,
  stipRequestItems,
  stipsLocalState,
  assetsLocalState,
  fileName,
  handleFile,
  removeFile,
  handleChange,
  handleAssetChange,
}: AdditionalItemsProps): React.ReactNode {

  const filteredStipTypeOptions = useMemo(() => {
    const usedStipTypeIds = new Set(
      stipRequestItems.map((item) => String(item.stipTypeId))
    );

    return stipsTypes
      .filter(
        (stip) =>
          typeof stip.stipTypeName === 'string' &&
          !stip.stipTypeName.toLowerCase().startsWith('emergency contacts') &&
          !usedStipTypeIds.has(String(stip.stipTypeId))
      )
      .sort((a, b) => a.stipTypeName.localeCompare(b.stipTypeName));
  }, [stipsTypes, stipRequestItems]);

  return (
    <div
      className={classNames('document-form__additional-items', {
        'document-form__additional-items--list': data[fileName],
      })}
    >
      {!data[fileName] ? (
        <div className="document-form__additional-items__dropdowns">
          <StipsTypeSelect
            selectValue={stipsLocalState.stipsType}
            handleChange={(event) => handleChange(event, fileName)}
            stipsTypes={filteredStipTypeOptions}
            title="Select Document Type"
            filledInputName="stipsType"
            htmlFor="filled-stipsType-simple"
          />

          {selectedStipTypeId !== null && selectedStipTypeId !== undefined ? (
            <AssetSelect
              selectValue={assetsLocalState ?? ''}
              handleChange={(event) => handleAssetChange(event, fileName)}
              assets={generateAssets(selectedStipTypeId, assets, assetDocuments)}
              title={generateTitle(selectedStipTypeId)}
              filledInputName="assets"
              htmlFor="filled-assets-simple"
            />
          ) : null}
        </div>
      ) : (
        <div className="document-form__additional-items--info">
          <div className="document-form__doc-type">{stipsLocalState.stipData?.stipTypeName}</div>
        </div>
      )}

      <div className="document-form__additional-items__button">
        <AdditionalItemsUpload
          data={data}
          fileName={fileName}
          handleFile={handleFile}
          removeFile={removeFile}
        />
      </div>
    </div>
  );
}

AdditionalItems.displayName = 'AdditionalItems';
